/**
 * The lines of a part drawn from one event to another, as playback reads them.
 *
 * A slur and a hairpin are both kept as two event ids, and both are read as the part is
 * played: opened where the first is reached and closed where the second is. Both also have to
 * know the bars they cover, because the played order is not the written one and may leave a
 * line before its last event is ever reached.
 */

import type { EventId, SpannerId } from "../document/ids";
import type { Part, Score, Spanner, SpannerKind } from "../document/types";

/** The spanners of a part of some kinds, by where each begins and ends. */
export interface Spans {
  readonly opens: ReadonlyMap<EventId, readonly Spanner[]>;
  readonly closes: ReadonlyMap<EventId, readonly Spanner[]>;
  /**
   * The bar each one's last event stands in, an index of {@link Score.measures}. None for one
   * whose last event is not in the part.
   */
  readonly lastBar: ReadonlyMap<SpannerId, number>;
}

export function spansOf(score: Score, part: Part, kinds: readonly SpannerKind[]): Spans {
  const opens = new Map<EventId, Spanner[]>();
  const closes = new Map<EventId, Spanner[]>();
  for (const span of part.spanners) {
    if (!kinds.includes(span.kind)) continue;
    opens.set(span.from, [...(opens.get(span.from) ?? []), span]);
    closes.set(span.to, [...(closes.get(span.to) ?? []), span]);
  }

  const lastBar = new Map<SpannerId, number>();
  score.measures.forEach((measure, index) => {
    for (const voice of part.measures[measure.id]?.voices ?? []) {
      for (const event of voice.events) {
        for (const span of closes.get(event.id) ?? []) lastBar.set(span.id, index);
      }
    }
  });
  return { opens, closes, lastBar };
}
